import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { loginRequest } from "../authConfig";
import { useTheme } from "../contexts/ThemeContext";

export default function LoginPage() {
  const { instance, inProgress } = useMsal();
  const { isDark } = useTheme();

  const isProcessing =
    inProgress === InteractionStatus.HandleRedirect ||
    inProgress === InteractionStatus.Login ||
    inProgress === InteractionStatus.Startup;

  const handleLogin = () => {
    instance.loginRedirect(loginRequest).catch((error: unknown) => {
      console.error("[MSAL] loginRedirect Fehler:", error);
    });
  };

  if (isProcessing) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "var(--bg-page)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: "1rem",
        }}
      >
        <div
          style={{
            width: "48px",
            height: "48px",
            border: "4px solid var(--border)",
            borderTop: "4px solid #1B6AB1",
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <p style={{ color: "var(--text-secondary)", fontSize: "0.95rem", margin: 0 }}>
          Anmeldung wird verarbeitet…
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--bg-page)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
      }}
    >
      <div style={{ width: "100%", maxWidth: "420px" }}>
        {/* Card */}
        <div
          style={{
            background: "var(--bg-card)",
            border: "1px solid var(--border)",
            borderRadius: "16px",
            padding: "2.5rem",
            boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
          }}
        >
          {/* Logo */}
          <div style={{ textAlign: "center", marginBottom: "2rem" }}>
            <img
              src={isDark ? "/brand/aware-logo-dark.png" : "/brand/aware-logo.png"}
              alt="AWARE by SHERLOX"
              style={{ height: "64px", width: "auto", display: "block", margin: "0 auto 1rem" }}
            />
            <p style={{ fontSize: "0.8rem", color: "var(--text-muted)", margin: 0, textTransform: "uppercase", letterSpacing: "0.08em" }}>
              Security Awareness Platform
            </p>
          </div>

          {/* Feature list */}
          <div
            style={{
              background: "var(--bg-page)",
              border: "1px solid var(--border)",
              borderRadius: "10px",
              padding: "1rem 1.25rem",
              marginBottom: "1.75rem",
            }}
          >
            <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "0.4rem" }}>
              {[
                "Phishing-Simulationen für Ihr Team",
                "Micro-Learning & Security-Quizze",
                "Echtzeit-Risiko-Dashboard",
              ].map((item) => (
                <li key={item} style={{ display: "flex", alignItems: "center", gap: "0.625rem", color: "var(--text-medium)", fontSize: "0.875rem" }}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#1B6AB1" width="16" height="16">
                    <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                  </svg>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Login button */}
          <button
            onClick={handleLogin}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.75rem",
              padding: "0.875rem 1.5rem",
              background: "#1B6AB1",
              color: "white",
              border: "none",
              borderRadius: "10px",
              fontSize: "1rem",
              fontWeight: "600",
              cursor: "pointer",
              transition: "background 0.15s ease",
            }}
            onMouseOver={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "#154E8A"; }}
            onMouseOut={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "#1B6AB1"; }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 23 23" width="20" height="20">
              <rect x="1" y="1" width="10" height="10" fill="#f25022" />
              <rect x="12" y="1" width="10" height="10" fill="#7fba00" />
              <rect x="1" y="12" width="10" height="10" fill="#00a4ef" />
              <rect x="12" y="12" width="10" height="10" fill="#ffb900" />
            </svg>
            Mit Microsoft anmelden
          </button>

          {/* Divider */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", margin: "1.25rem 0" }}>
            <div style={{ flex: 1, height: "1px", background: "var(--border)" }} />
            <span style={{ fontSize: "0.7rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>oder</span>
            <div style={{ flex: 1, height: "1px", background: "var(--border)" }} />
          </div>

          {/* Demo access */}
          <a
            href="/demo"
            style={{
              width: "100%",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.6rem",
              padding: "0.8rem 1.5rem",
              background: "transparent",
              color: "#1B6AB1",
              border: "1px solid #1B6AB1",
              borderRadius: "10px",
              fontSize: "0.95rem",
              fontWeight: "600",
              cursor: "pointer",
              textDecoration: "none",
              transition: "background 0.15s ease",
            }}
            onMouseOver={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = "rgba(27,106,177,0.06)"; }}
            onMouseOut={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = "transparent"; }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
              <path d="M5 3l14 9-14 9V3z" />
            </svg>
            Demo ansehen
          </a>

          <p style={{ textAlign: "center", fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "1.25rem", marginBottom: 0 }}>
            Anmeldung nur für autorisierte Unternehmensnutzer · Demo ohne Anmeldung
          </p>

          {/* Anbieterkennzeichnung nach § 5 DDG – muss ohne Login erreichbar sein */}
          <div style={{ marginTop: "1.5rem", paddingTop: "1.25rem", borderTop: "1px solid var(--border)", textAlign: "center" }}>
            <a
              href="/impressum"
              style={{ fontSize: "0.75rem", color: "var(--text-secondary)", textDecoration: "underline", textUnderlineOffset: "3px" }}
            >
              Impressum
            </a>
          </div>
        </div>

        <p style={{ textAlign: "center", fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "1.5rem" }}>
          © {new Date().getFullYear()} SHERLOX AWARE · Alle Rechte vorbehalten
        </p>
      </div>
    </div>
  );
}
